import React from 'react';
import {
  MPFCardCarousel,
  type MPFCardCarouselSlide,
} from 'volto-ministerio-publico-federal/components/UI';
import { OndeAtuaCard } from './OndeAtuaCard';
import { OndeAtuaTemaCard } from './OndeAtuaTemaCard';
import type { OndeAtuaTema, YoutubeVideoRef } from './helpers';

interface OndeAtuaCarouselMobileProps {
  tema: OndeAtuaTema;
  isEditMode?: boolean;
  onPlay: (video: YoutubeVideoRef) => void;
  /** Presente só no editor: recebe o índice do card em `tema{N}Cards`. */
  onEditItem?: (index: number) => void;
}

/**
 * A faixa "Onde o MPF atua?" abaixo de `lg`: um carrossel só, para celular E
 * tablet.
 *
 * No desktop (`lg`+) o texto do tema é a coluna da esquerda e os cards passam
 * num carrossel à direita. Abaixo disso não há duas colunas — empilhar o
 * texto acima de um carrossel de fotos foi a 1ª correção deste bloco, mas o
 * design pediu outra coisa para o tablet: reaproveitar a esteira do celular,
 * só que mostrando mais de um card por vez. Aqui os dois (celular e tablet)
 * viram a mesma esteira: o texto é o primeiro slide, os cards vêm depois, e
 * os bullets contam o conjunto inteiro.
 *
 * `slidesPerView` varia por `breakpoints` do Swiper (chave em px, não a
 * classe do Tailwind — os dois só coincidem em valor):
 *   - abaixo de 768px (celular): 1 slide por vez, largura total — é o
 *     `slidesPerView` "base" (prop abaixo).
 *   - 768px+ (tablet — este ramo já está `lg:hidden` no pai, então na
 *     prática só vale até 1024px): ~2,3 slides visíveis, com o 3º cortado na
 *     borda direita (peek), sinalizando que dá pra rolar — pedido do mockup
 *     do tablet. Card mais largo que no celular, então o `fullWidth` do
 *     botão (ver `OndeAtuaTemaCard`) tem espaço de sobra para caber inteiro.
 *
 * Sem setas (`showArrows={false}`): no toque quem navega é o dedo, e os
 * bullets do Swiper já são focáveis pelo teclado (módulo `A11y`).
 *
 * O `name` é `atua-mobile`, e não `atua`: o carrossel do desktop existe no
 * mesmo DOM (só escondido por CSS), e nomes iguais fariam os dois disputarem a
 * mesma paginação — é a colisão contra a qual `OndeAtuaCarousel` já avisa.
 */
export const OndeAtuaCarouselMobile: React.FC<OndeAtuaCarouselMobileProps> = ({
  tema,
  isEditMode,
  onPlay,
  onEditItem,
}) => {
  // Um tema pode ter só texto (ver `temaHasContent`). Aí não há esteira: um
  // slide sozinho não tem para onde ir, e a fileira de bullets ficaria com um
  // ponto só.
  if (tema.cards.length === 0) {
    return <OndeAtuaTemaCard tema={tema} />;
  }

  const slides: MPFCardCarouselSlide[] = [
    { key: 'tema', content: <OndeAtuaTemaCard tema={tema} /> },
    // `onEditItem` vai inteiro: o card já carrega o `sourceIndex` e passa a
    // posição certa — a mesma mecânica do carrossel do desktop.
    ...tema.cards.map((card) => ({
      key: card.key,
      content: (
        <OndeAtuaCard
          card={card}
          isEditMode={isEditMode}
          onPlay={onPlay}
          onEditItem={onEditItem}
        />
      ),
    })),
  ];

  return (
    <MPFCardCarousel
      name="atua-mobile"
      slides={slides}
      slidesPerView={1}
      spaceBetween={16}
      // Peek do tablet: ver o comentário do componente sobre o corte em
      // 768px. `slidesPerGroup` não é setado — o padrão (1) mantém a
      // paginação com um bullet por slide, como no carrossel do desktop.
      breakpoints={{ 768: { slidesPerView: 2.3, spaceBetween: 16 } }}
      showArrows={false}
      labels={{
        prev: 'Destaque anterior',
        next: 'Próximo destaque',
        first: 'Este é o primeiro destaque',
        last: 'Este é o último destaque',
        bullet: 'Ir para o destaque {{index}}',
      }}
    />
  );
};

export default OndeAtuaCarouselMobile;
